import { Translation } from '@ps/web-shared-components';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import type { LocationStateProps } from '@swf/casino';
import FormattedTimeZone from '@swf/components/FormattedTimeZone/FormattedTimeZone';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Body from '@swf/components/StarsModal/components/Body';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import { StatusCode } from '@swf/core/enums/statusCode';
import useTrackers from '@swf/core/hooks/useTrackers';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { JoinReason } from '@swf/core/types/CasinoGame';
import { statusKey } from '@swf/core/utils';
import { ServerMessageHandler } from '@swf/notification';

import {
  TXTACL_We_are_sorryX_an_unexpected_er_ELL,
  TXTCLI_Login,
  TXTCLI_OK,
  TXTCLI_Resume_Playing,
  TXTWCL_Interrupted_games,
  TXTWCL_Sorry___an_error_has_occurred,
  TXTWCL_You_have_an_interrupted_spin_o_ELL
} from './CasinoError.messages.json';
import styles from './CasinoError.scss';

const buttonTag = {
  [StatusCode.NO_SESSION_ERROR]: TXTCLI_Login,
  [StatusCode.SESSION_AUTH_ERROR]: TXTCLI_Login,
  [StatusCode.SESSION_CLOSED_ERROR]: TXTCLI_Login,
  [StatusCode.INTERRUPTED_GAME_ERROR]: TXTCLI_Resume_Playing
};

const statusCodeTitle = {
  [StatusCode.INTERRUPTED_GAME_ERROR]: <Translation {...TXTWCL_Interrupted_games} />
};

const messageLookup = {
  [StatusCode.UNFINISHED_GAMES_ON_ANOTHER_DEVICE]: (
    <Translation tagName="p" {...TXTWCL_You_have_an_interrupted_spin_o_ELL} />
  )
};

const CasinoError: React.FC = () => {
  const { errorViewed } = useTrackers();
  const {
    state: { game, title, message, statusCode, data, dateTime }
  } = useLocation<LocationStateProps>();
  const {
    modal: { enqueue, close },
    casino: { play, reconnect },
    auth: { logout }
  } = useDispatch<Dispatch>();
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);

  useEffect(() => errorViewed({ errorCode: statusKey(statusCode) }), [errorViewed, statusCode]);

  const handleClick = useCallback(async () => {
    switch (statusCode) {
      case StatusCode.NO_SESSION_ERROR:
      case StatusCode.SESSION_AUTH_ERROR:
      case StatusCode.SESSION_CLOSED_ERROR:
        logout({});
        enqueue({ path: '/auth/login' });
        break;
      case StatusCode.INTERRUPTED_GAME_ERROR:
        if (isDesktopEmbedded) {
          (data?.intGms || []).forEach((g) =>
            play({ game, table: g, joinReason: JoinReason.Resume })
          );
          (data?.liveT || []).forEach((g) => reconnect({ instance: g }));
        } else {
          if (data?.intGms?.length) {
            await play({ game, table: data.intGms[0], joinReason: JoinReason.Resume });
          } else if (data?.liveT?.length) {
            await reconnect({ instance: data.liveT[0] });
          }
        }
        break;
      case StatusCode.MULTI_PLATFORM_LOGIN:
        logout({});
        break;
    }
    close();
  }, [close, game, data, enqueue, isDesktopEmbedded, logout, play, reconnect, statusCode]);

  const values = dateTime ? { 0: <FormattedTimeZone date={dateTime} /> } : {};

  return (
    <>
      <Header>
        {statusCodeTitle[statusCode] || (
          <Translation {...(title || TXTWCL_Sorry___an_error_has_occurred)} />
        )}
        <CloseButton />
      </Header>
      <Body className={styles.body}>
        {messageLookup[statusCode] ||
          (message ? (
            typeof message === 'string' ? (
              <p>
                <ServerMessageHandler message={message} />
              </p>
            ) : (
              <Translation tagName="p" {...message} values={values} />
            )
          ) : (
            <Translation tagName="p" {...TXTACL_We_are_sorryX_an_unexpected_er_ELL} />
          ))}
        <StarsButton
          onClick={handleClick}
          translation={buttonTag[statusCode] || TXTCLI_OK}
          className={styles.button}
        />
      </Body>
    </>
  );
};

export default CasinoError;
